import type { ReactNode } from 'react'
import type { SectorPerformanceRow } from '@/lib/market/builders/statistics'

import Link from 'next/link'
import { divergingFill } from '@/components/market/market-map'
import { formatPercent } from '@/lib/market/format'

const HORIZONS: Array<{ key: 'd1' | 'w1' | 'm1' | 'm3' | 'm6' | 'ytd' | 'y1', label: string, limit: number }> = [
    { key: 'd1', label: 'Today', limit: 3 },
    { key: 'w1', label: '1W', limit: 6 },
    { key: 'm1', label: '1M', limit: 10 },
    { key: 'm3', label: '3M', limit: 18 },
    { key: 'm6', label: '6M', limit: 25 },
    { key: 'ytd', label: 'YTD', limit: 30 },
    { key: 'y1', label: '1Y', limit: 35 }
]

const SectorMatrix = ({ sectors }: { sectors: SectorPerformanceRow[] }): ReactNode => {
    const rows = [...sectors].sort((a, b) => (b.perf.ytd ?? 0) - (a.perf.ytd ?? 0))

    return (
        <div className='overflow-x-auto scroll-thin'>
            <table className='w-full min-w-[720px] border-separate border-spacing-[2px] text-[13px]'>
                <caption className='sr-only'>Sector performance across horizons, ordered by year-to-date return</caption>
                <thead>
                    <tr className='text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                        <th scope='col' className='py-2 pr-3 text-left font-medium'>Sector</th>
                        {HORIZONS.map(horizon => <th key={horizon.key} scope='col' className='py-2 text-center font-medium'>{horizon.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(sector => (
                        <tr key={sector.slug}>
                            <th scope='row' className='py-1.5 pr-3 text-left font-normal'>
                                <Link href={`/equities?sector=${sector.slug}`} className='text-foreground hover:text-primary'>
                                    {sector.id ? <span data-mdlfy-cms={`sectors:${sector.id}:name`}>{sector.name}</span> : sector.name}
                                </Link>
                                <span className='block text-[11px] text-muted-foreground'>{sector.members} {sector.members === 1 ? 'company' : 'companies'}</span>
                            </th>
                            {HORIZONS.map(horizon => {
                                const value = horizon.key === 'd1' ? sector.d1 : sector.perf[horizon.key]

                                if (value === null) {
                                    return <td key={horizon.key} className='rounded-[4px] bg-secondary text-center text-muted-foreground'>-</td>
                                }

                                const fill = divergingFill(value, horizon.limit)

                                return (
                                    <td key={horizon.key} className='h-10 rounded-[4px] text-center text-xs font-medium tabular-nums' style={{ background: fill.background, color: fill.ink }}>
                                        {formatPercent(value, 1)}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default SectorMatrix
